import { z } from "zod";

import { ALL_CALCULATIONS, type Calculation } from "~/tools/calculate-value-from-points";
import { ImmutableSet } from "~/tools/immutable-set";
import {
	type GetImpulseFormInput,
	ImpulseFormOptional,
	ImpulseFormShape,
	ImpulseFormSwitch,
	ImpulseFormUnit,
} from "~/tools/impulse-form";
import { isPresent } from "~/tools/is-present";
import { isSetDirty } from "~/tools/is-set-dirty";
import type { Partial } from "~/tools/partial";

const BOTTOM_POSITION = "bottom";
const ALL_POSITIONS = [BOTTOM_POSITION, "right"] as const;
const positionSchema = z.enum(ALL_POSITIONS);
type WidgetLegendPositionVariant = z.infer<typeof positionSchema>;
type WidgetLegendPosition = ImpulseFormUnit<string, ReadonlyArray<string>, WidgetLegendPositionVariant>;

const LIST_MODE = "list";
const ALL_MODES = [LIST_MODE, "table"] as const;
const modeSchema = z.enum(ALL_MODES);
type WidgetLegendModeVariant = z.infer<typeof modeSchema>;
type WidgetLegendMode = ImpulseFormUnit<string, ReadonlyArray<string>, WidgetLegendModeVariant>;

type WidgetLegendTable = ImpulseFormUnit<ReadonlyArray<string>, ReadonlyArray<string>, ImmutableSet<Calculation>>;
const tableSchema = z.array(z.enum(ALL_CALCULATIONS)).transform(ImmutableSet.fromArray);

type WidgetLegend = ImpulseFormOptional<
	ImpulseFormUnit<boolean>,
	ImpulseFormShape<{
		position: WidgetLegendPosition;
		mode: ImpulseFormSwitch<
			WidgetLegendMode,
			{
				list: ImpulseFormUnit<true>;
				table: WidgetLegendTable;
			}
		>;
	}>
>;

const DEFAULT = {
	enabled: true,
	element: {
		position: BOTTOM_POSITION,
		mode: {
			active: LIST_MODE,
			branches: {
				list: true,
				table: [] as Array<string>,
			},
		},
	},
} satisfies GetImpulseFormInput<WidgetLegend>;

function init(initial?: Partial<GetImpulseFormInput<WidgetLegend>, "deep">): WidgetLegend {
	return ImpulseFormOptional(
		ImpulseFormUnit(initial?.enabled ?? DEFAULT.enabled),
		ImpulseFormShape({
			position: ImpulseFormUnit(initial?.element?.position ?? DEFAULT.element.position, {
				schema: positionSchema,
			}),
			mode: ImpulseFormSwitch(
				ImpulseFormUnit(initial?.element?.mode?.active ?? DEFAULT.element.mode.active, {
					schema: modeSchema,
				}),
				{
					list: ImpulseFormUnit(true),
					table: ImpulseFormUnit(
						initial?.element?.mode?.branches?.table?.filter(isPresent) ?? DEFAULT.element.mode.branches.table,
						{
							schema: tableSchema,
							isInputDirty: isSetDirty,
						},
					),
				},
			),
		}),
	);
}

const WidgetLegend = {
	default: DEFAULT,
	allPositions: ALL_POSITIONS,
	allModes: ALL_MODES,
	init,
} as const;

export { WidgetLegend };
export type {
	WidgetLegendPositionVariant,
	WidgetLegendModeVariant,
	WidgetLegendPosition,
	WidgetLegendMode,
	WidgetLegendTable,
};
